import { useState, useEffect } from 'react'
import api from '../../utils/api'
import Button from '../ui/Button'
import Alert from '../ui/Alert'
import { useAuth } from '../../hooks/useAuth'
import ar from '../../locales/ar.json'

const ta = ar.app
const t  = ar.admin

function NeighborhoodList({ regionId, onRegionChange }) {
  const { user }  = useAuth()
  const isAdmin   = user?.role === 'admin'
  const [items,   setItems]   = useState([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')

  useEffect(() => {
    setLoading(true)
    api.get(`admin/regions/${regionId}/neighborhoods`)
      .then((r) => setItems(r.data || []))
      .catch(() => setError('تعذّر تحميل الأحياء'))
      .finally(() => setLoading(false))
  }, [regionId])

  async function handleAdd(e) {
    e.preventDefault()
    if (!newName.trim()) return
    setSaving(true)
    try {
      const { data } = await api.post(`admin/regions/${regionId}/neighborhoods`, { name: newName.trim() })
      setItems((p) => [...p, data])
      setNewName('')
      onRegionChange()
    } catch (err) {
      setError(err.message || t.errorSaving)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(nbId) {
    if (!window.confirm(t.confirmDeleteNeighborhood)) return
    try {
      await api.delete(`admin/regions/${regionId}/neighborhoods/${nbId}`)
      setItems((p) => p.filter((n) => n.id !== nbId))
      onRegionChange()
    } catch (err) {
      setError(err.message)
    }
  }

  if (loading) return <p className="text-stone-400 text-sm py-2">{ta.loading}</p>

  return (
    <div className="mt-3 pr-4 border-r border-stone-200">
      {error && <Alert type="error" message={error} className="mb-2" />}
      <ul className="space-y-1 mb-2">
        {items.length === 0 && <li className="text-stone-400 text-xs">لا توجد أحياء</li>}
        {items.map((nb) => (
          <li key={nb.id} className="flex items-center justify-between py-1 text-sm">
            <span className="text-stone-700">{nb.name}</span>
            {isAdmin && (
              <button
                type="button"
                onClick={() => handleDelete(nb.id)}
                className="text-xs text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
      {isAdmin && (
        <form onSubmit={handleAdd} className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="اسم الحي"
            className="input-base text-xs py-1.5 flex-1"
            dir="rtl"
          />
          <Button type="submit" variant="secondary" size="sm" loading={saving}>+ إضافة</Button>
        </form>
      )}
    </div>
  )
}

export default function RegionManager() {
  const { user } = useAuth()
  const isAdmin  = user?.role === 'admin'

  const [regions,   setRegions]   = useState([])
  const [expanded,  setExpanded]  = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [newName,   setNewName]   = useState('')
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState('')

  function fetchRegions() {
    return api.get('regions').then((r) => setRegions(r.data || []))
  }

  useEffect(() => {
    fetchRegions().catch(() => setError('تعذّر تحميل المناطق')).finally(() => setLoading(false))
  }, [])

  async function handleAddRegion(e) {
    e.preventDefault()
    if (!newName.trim()) return
    setSaving(true)
    try {
      const { data } = await api.post('admin/regions', { name: newName.trim() })
      setRegions((p) => [...p, data])
      setNewName('')
    } catch (err) {
      setError(err.message || t.errorSaving)
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteRegion(id) {
    if (!window.confirm(t.confirmDeleteRegion)) return
    try {
      await api.delete(`admin/regions/${id}`)
      setRegions((p) => p.filter((r) => r.id !== id))
      if (expanded === id) setExpanded(null)
    } catch (err) {
      setError(err.message)
    }
  }

  if (loading) return <div className="card text-center py-12 text-stone-400">{ta.loading}</div>

  return (
    <div className="max-w-xl">
      <h1 className="text-lg font-bold text-stone-800 mb-4">{t.regionsTitle}</h1>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {/* Add region */}
      {isAdmin && (
        <div className="card mb-4">
          <h3 className="font-semibold text-primary-700 mb-3 text-sm">{t.addRegion}</h3>
          <form onSubmit={handleAddRegion} className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="اسم المنطقة"
              className="input-base flex-1"
              dir="rtl"
            />
            <Button type="submit" variant="primary" loading={saving}>إضافة</Button>
          </form>
        </div>
      )}

      {/* Regions list */}
      <div className="card space-y-2">
        {regions.length === 0 && (
          <p className="text-stone-400 text-sm text-center py-4">لا توجد مناطق</p>
        )}
        {regions.map((r) => (
          <div key={r.id} className="border border-stone-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between bg-stone-50 px-4 py-3">
              <button
                type="button"
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                className="flex items-center gap-2 text-sm font-semibold text-stone-800 hover:text-primary-700"
              >
                <span>{expanded === r.id ? '▼' : '▶'}</span>
                {r.name}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => handleDeleteRegion(r.id)}
                  className="text-xs text-red-400 hover:text-red-600"
                >
                  حذف المنطقة
                </button>
              )}
            </div>
            {expanded === r.id && (
              <div className="px-4 py-3">
                <NeighborhoodList regionId={r.id} onRegionChange={fetchRegions} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
